// App shell: auth gate → landing (pick orders | upload CSV | past runs |
// deliveries) → progress → results → dispatch plan. Both build paths feed the same job
// pipeline; a result (fresh or past) opens its dispatch plan for finalizing.

import { type CSSProperties, useEffect, useState } from "react";

import {
  type FleetOptions,
  type OrderSelectionPayload,
  createPlanByHand,
  pollJob,
  submitEvaluateCsv,
  submitEvaluatePlan,
  submitOptimize,
  submitOptimizeFromOrders,
} from "./api/client";
import { type Session, authEnabled, getSession, onAuthChange, signOut } from "./auth/session";
import { useT } from "./lib/i18n";
import { CarrierCostsView } from "./components/CarrierCostsView";
import { DeliveriesView } from "./components/DeliveriesView";
import { DispatchPlanView } from "./components/DispatchPlanView";
import { HistoryView } from "./components/HistoryView";
import { LangToggle } from "./components/LangToggle";
import { LoginView } from "./components/LoginView";
import { CART_AUTOSAVE_KEY, type HandBuiltPlan, OrdersView } from "./components/OrdersView";
import { ProgressView } from "./components/ProgressView";
import { ResultsView } from "./components/ResultsView";
import { SavedPlansView } from "./components/SavedPlansView";
import { UploadView } from "./components/UploadView";
import type { JobStage, OptimizeResponseDTO, PlanSource } from "./types";

type Tab = "orders" | "upload" | "history" | "plans" | "deliveries" | "costs";

type View =
  | {
      name: "landing";
      tab: Tab;
      error: string | null;
      // Deliveries opened from a plan: that plan's depot and day.
      deliveries?: { depotZip: string; planDate: string };
    }
  | {
      name: "progress";
      message: string;
      elapsed?: number;
      budget?: number;
      stage?: JobStage | null;
    }
  | { name: "results"; result: OptimizeResponseDTO }
  | { name: "plan"; source: PlanSource; back: View; truckId?: string };

export function App() {
  // undefined = checking; null = auth on but logged out; Session = ready.
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [view, setView] = useState<View>({ name: "landing", tab: "orders", error: null });
  // The plan page reports unsaved edits so leaving the app (sign-out) can warn.
  const [planDirty, setPlanDirty] = useState(false);
  const t = useT();

  useEffect(() => {
    let alive = true;
    void getSession().then((s) => alive && setSession(s));
    const unsub = onAuthChange(() => void getSession().then((s) => alive && setSession(s)));
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  const tab = view.name === "landing" ? view.tab : "orders";

  async function run(submit: () => Promise<{ job_id: string }>, backTab: Tab) {
    setView({ name: "progress", message: t("app.submitting") });
    try {
      const { job_id } = await submit();
      // The engine states the fallback pass once and then talks about other
      // things for twenty messages, so carry the last known stage forward
      // and merge. A diagnostic line must not blank the stepper.
      let held: JobStage | null = null;
      const result = await pollJob(job_id, (status) => {
        if (status.stage) {
          held = {
            ...status.stage,
            fallback_pass: status.stage.fallback_pass ?? held?.fallback_pass ?? null,
          };
        }
        setView({
          name: "progress",
          message: status.progress || t("app.optimizing"),
          elapsed: status.elapsed_seconds ?? undefined,
          budget: status.budget_seconds ?? undefined,
          stage: held,
        });
      });
      // The run succeeded — drop the autosaved cart so "New plan" starts fresh.
      // (On error we fall through to the catch, leaving it for recovery.)
      try {
        localStorage.removeItem(CART_AUTOSAVE_KEY);
      } catch {
        /* storage disabled — ignore */
      }
      setView({ name: "results", result });
    } catch (err) {
      setView({
        name: "landing",
        tab: backTab,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  function fromOrders(orders: OrderSelectionPayload[], fleet: FleetOptions) {
    void run(() => submitOptimizeFromOrders(orders, fleet), "orders");
  }
  function fromOrdersEvaluate(orders: OrderSelectionPayload[], fleet: FleetOptions) {
    void run(() => submitEvaluatePlan(orders, fleet), "orders");
  }
  // A draft plan from the cart with nothing optimized: opened once made, the
  // cart cleared; on a refusal the cart stays for another go.
  async function byHand(plan: HandBuiltPlan) {
    setView({ name: "progress", message: t("app.creatingPlan") });
    try {
      const created = await createPlanByHand(plan.orders, plan.planDate, plan.document);
      try {
        localStorage.removeItem(CART_AUTOSAVE_KEY);
      } catch {
        /* storage disabled — ignore */
      }
      setView({
        name: "plan",
        source: { plan_id: created.id },
        back: { name: "landing", tab: "orders", error: null },
      });
    } catch (err) {
      setView({
        name: "landing",
        tab: "orders",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  function fromCsv(file: File, fleet: FleetOptions) {
    void run(() => submitOptimize(file, fleet), "upload");
  }
  function fromCsvEvaluate(file: File, fleet: FleetOptions) {
    void run(() => submitEvaluateCsv(file, fleet), "upload");
  }

  if (session === undefined) {
    return <div style={{ padding: 40, opacity: 0.6 }}>{t("app.loading")}</div>;
  }
  if (authEnabled && session === null) {
    return <LoginView />;
  }

  return (
    <>
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" />
          {t("app.brand")}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <LangToggle />
          {authEnabled && session && (
            <button
              className="btn btn-ghost"
              onClick={() => {
                if (planDirty && !window.confirm(t("plan.discardConfirm"))) return;
                void signOut();
              }}
            >
              {t("app.signOut")}
            </button>
          )}
        </div>
      </header>

      {view.name === "landing" && (
        <>
          <nav style={tabBar}>
            <button
              style={tabBtn(tab === "orders")}
              onClick={() => setView({ name: "landing", tab: "orders", error: null })}
            >
              {t("app.tabOrders")}
            </button>
            <button
              style={tabBtn(tab === "upload")}
              onClick={() => setView({ name: "landing", tab: "upload", error: null })}
            >
              {t("app.tabUpload")}
            </button>
            <button
              style={tabBtn(tab === "history")}
              onClick={() => setView({ name: "landing", tab: "history", error: null })}
            >
              {t("app.tabHistory")}
            </button>
            <button
              style={tabBtn(tab === "plans")}
              onClick={() => setView({ name: "landing", tab: "plans", error: null })}
            >
              {t("app.tabPlans")}
            </button>
            <button
              style={tabBtn(tab === "deliveries")}
              onClick={() => setView({ name: "landing", tab: "deliveries", error: null })}
            >
              {t("app.tabDeliveries")}
            </button>
            <button
              style={tabBtn(tab === "costs")}
              onClick={() => setView({ name: "landing", tab: "costs", error: null })}
            >
              {t("app.tabCosts")}
            </button>
          </nav>
          {tab === "orders" && (
            <OrdersView
              onSubmit={fromOrders}
              onEvaluate={fromOrdersEvaluate}
              onPlanByHand={(plan) => void byHand(plan)}
              onOpenPlan={(planId, truckId) =>
                setView({
                  name: "plan",
                  source: { plan_id: planId },
                  back: { name: "landing", tab: "orders", error: null },
                  truckId,
                })
              }
              errorMessage={view.error}
            />
          )}
          {tab === "upload" && (
            <UploadView
              onSubmit={fromCsv}
              onEvaluate={fromCsvEvaluate}
              errorMessage={view.error}
            />
          )}
          {tab === "deliveries" && (
            <DeliveriesView
              initial={view.deliveries}
              onOpenPlan={(planId, truckId) =>
                setView({
                  name: "plan",
                  source: { plan_id: planId },
                  back: { name: "landing", tab: "deliveries", error: null },
                  truckId,
                })
              }
            />
          )}
          {tab === "costs" && (
            <CarrierCostsView
              onOpenPlan={(planId, truckId) =>
                setView({
                  name: "plan",
                  source: { plan_id: planId },
                  back: { name: "landing", tab: "costs", error: null },
                  truckId,
                })
              }
            />
          )}
          {tab === "plans" && (
            <SavedPlansView
              onOpenPlan={(planId) =>
                setView({
                  name: "plan",
                  source: { plan_id: planId },
                  back: { name: "landing", tab: "plans", error: null },
                })
              }
            />
          )}
          {tab === "history" && (
            <HistoryView
              onOpenPlan={(runId) =>
                setView({
                  name: "plan",
                  source: { run_id: runId },
                  back: { name: "landing", tab: "history", error: null },
                })
              }
            />
          )}
        </>
      )}
      {view.name === "progress" && (
        <ProgressView
          message={view.message}
          elapsed={view.elapsed}
          budget={view.budget}
          stage={view.stage}
        />
      )}
      {view.name === "results" && (
        <ResultsView
          result={view.result}
          onNewPlan={() => setView({ name: "landing", tab: "orders", error: null })}
          onFinalize={
            view.result.job_id
              ? () => setView({ name: "plan", source: { job_id: view.result.job_id! }, back: view })
              : undefined
          }
        />
      )}
      {view.name === "plan" && (
        <DispatchPlanView
          source={view.source}
          onBack={() => setView(view.back)}
          onDirtyChange={setPlanDirty}
          onSavedPlans={() => setView({ name: "landing", tab: "plans", error: null })}
          onTrackDeliveries={(depotZip, planDate) =>
            setView({
              name: "landing",
              tab: "deliveries",
              error: null,
              deliveries: { depotZip, planDate },
            })
          }
          onOpenPlan={(planId) =>
            setView({ name: "plan", source: { plan_id: planId }, back: view.back })
          }
          focusTruckId={view.truckId}
        />
      )}
    </>
  );
}

const tabBar: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  justifyContent: "center",
  margin: "16px auto 0",
};

function tabBtn(active: boolean): CSSProperties {
  return {
    padding: "8px 18px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.15)",
    cursor: "pointer",
    fontWeight: 600,
    background: active ? "var(--accent, #3884ff)" : "transparent",
    color: active ? "#fff" : "inherit",
  };
}
